import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      toast.error('Invalid payment session');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    const checkStatus = async (attemptCount) => {
      if (attemptCount >= maxAttempts) {
        setStatus('failed');
        toast.error('Payment verification timeout. Please contact us to confirm your booking.');
        return;
      }

      try {
        const response = await axios.get(
          `${API}/payments/checkout/status/${sessionId}?origin_url=${window.location.origin}`
        );

        if (response.data.success) {
          const paymentStatus = response.data.payment_status;
          
          if (paymentStatus === 'paid') {
            setStatus('success');
            setPaymentDetails(response.data);
            toast.success('Payment successful! Your booking is confirmed.');
            return;
          } else if (paymentStatus === 'expired' || paymentStatus === 'failed') {
            setStatus('failed');
            toast.error('Payment was not completed. Please try again.');
            return;
          }

          // Payment still pending, continue polling
          setAttempts(attemptCount + 1);
          setTimeout(() => checkStatus(attemptCount + 1), pollInterval);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('failed');
        toast.error('Error verifying payment. Please contact us.');
      }
    };

    await checkStatus(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full shadow-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold">
            {status === 'checking' && 'Verifying Payment...'}
            {status === 'success' && 'Payment Successful!'}
            {status === 'failed' && 'Payment Issue'}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="text-center space-y-6 p-8">
          {status === 'checking' && (
            <div className="space-y-4">
              <Loader2 className="w-16 h-16 text-teal-600 animate-spin mx-auto" />
              <p className="text-gray-600 text-lg">
                Please wait while we confirm your payment...
              </p>
              <p className="text-sm text-gray-500">
                Attempt {attempts + 1} of 10
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <CheckCircle2 className="w-24 h-24 text-green-500 mx-auto" />
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Thank You for Your Payment!
                </h3>
                <p className="text-gray-600 text-lg">
                  Your laundry pickup has been confirmed and paid.
                </p>
              </div>

              {paymentDetails && (
                <div className="bg-gray-50 rounded-lg p-6 text-left">
                  <h4 className="font-semibold text-gray-900 mb-3">Payment Details:</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount Paid:</span>
                      <span className="font-semibold">
                        ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-semibold text-green-600">Paid</span>
                    </div>
                    {paymentDetails.metadata?.package_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Package:</span>
                        <span className="font-semibold">{paymentDetails.metadata.package_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <p className="text-teal-800">
                  <strong>What's Next?</strong><br />
                  We'll contact you shortly to confirm your pickup time. 
                  You'll receive updates via phone/email.
                </p>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => navigate('/')}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  Back to Home
                </Button>
                <Button
                  onClick={() => navigate('/#book-now')}
                  variant="outline"
                  className="border-teal-600 text-teal-600 hover:bg-teal-50"
                >
                  Book Another Pickup
                </Button>
              </div>
            </div>
          )}

          {status === 'failed' && (
            <div className="space-y-6">
              <XCircle className="w-24 h-24 text-red-500 mx-auto" />
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Payment Not Completed
                </h3>
                <p className="text-gray-600 text-lg">
                  Your payment was not processed. Your booking is still active, but not paid.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">
                  <strong>Don't worry!</strong><br />
                  You can still pay during pickup or try booking again with payment.
                </p>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => navigate('/#book-now')}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-gray-600 text-gray-600 hover:bg-gray-50"
                >
                  Back to Home
                </Button>
              </div>

              <div className="text-sm text-gray-600">
                <p>Need help? Call us at: <a href="tel:762-436-2067" className="text-teal-600 font-semibold">762-436-2067</a></p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
